import { useState, useEffect } from 'react';
import { Icon } from './Icons.jsx';
import { MobHeader, FooterBar } from './Stepper.jsx';
import { searchLabs, searchLabCombinations } from '../api/labs.js';

export default function Step4Labs({ state, setState, onNext, onBack }) {
  const exams = state.exams || [];
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(state.lab || null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('distance');
  // Nenhum laboratório faz tudo: combinações sugeridas pela API
  const [combos, setCombos] = useState(null); // { missing, combinations } | null
  const [comboIdx, setComboIdx] = useState(state.combo ? state.combo.idx : null);

  useEffect(() => {
    searchLabs({
      tussCodes: exams.map(e => e.code),
      cep: state.location?.cep,
      radiusKm: state.location?.radius,
    })
      .then(data => {
        setLabs((data || []).map(r => ({
          id: r.partner.user_id,
          name: r.partner.trade_name,
          address: [r.partner.address, r.partner.neighborhood, r.partner.city].filter(Boolean).join(', '),
          hours: typeof r.partner.hours_json === 'string' ? r.partner.hours_json : null,
          phone: r.partner.phone,
          distanceKm: r.partner.distance_km,
          prices: Object.fromEntries((r.prices || []).map(p => [p.tuss_code, Number(p.price)])),
          total: Number(r.total_for_order),
          savings: Number(r.savings_pct) || 0,
        })));
      })
      .catch(() => setError('Não foi possível buscar laboratórios. Verifique sua conexão e tente novamente.'))
      .finally(() => setLoading(false));
  }, []);

  // Sem laboratório com o pedido inteiro → busca combinações
  useEffect(() => {
    if (loading || error || labs.length > 0 || combos) return;
    setLoading(true);
    searchLabCombinations({ tussCodes: exams.map(e => e.code), cep: state.location?.cep })
      .then(data => setCombos(data))
      .catch(() => setCombos({ missing: [], combinations: [] }))
      .finally(() => setLoading(false));
  }, [loading, error, labs.length]);

  const pickCombo = (combo, idx) => {
    setComboIdx(idx);
    setSelected(null);
    const parts = combo.labs.map(l => ({
      id: l.partner.user_id,
      name: l.partner.trade_name,
      address: [l.partner.address, l.partner.neighborhood, l.partner.city].filter(Boolean).join(', '),
      phone: l.partner.phone,
      distanceKm: l.partner.distance_km,
      codes: l.exams.map(e => e.tuss_code),
      prices: Object.fromEntries(l.exams.map(e => [e.tuss_code, Number(e.price)])),
      subtotal: Number(l.subtotal),
    }));
    setState(s => ({
      ...s,
      lab: null,
      labData: null,
      combo: { idx, parts, total: Number(combo.total), bloodTogether: combo.blood_together, bloodLabCount: combo.blood_lab_count, missing: combos?.missing || [] },
    }));
  };

  const pick = (lab) => {
    setSelected(lab.id);
    setComboIdx(null);
    setState(s => ({ ...s, lab: lab.id, labData: lab, combo: null }));
  };

  // A API já devolve `labs` ordenado por proximidade — "Mais próximo" é a
  // ordem original; os outros dois critérios reordenam por preço no cliente
  // (lista pequena, já toda carregada — sem necessidade de ida ao servidor).
  const filtered = labs.filter(l => l.name.toLowerCase().includes(q.trim().toLowerCase()));
  const sorted = sort === 'price-asc'
    ? [...filtered].sort((a, b) => a.total - b.total)
    : sort === 'price-desc'
      ? [...filtered].sort((a, b) => b.total - a.total)
      : filtered;

  return (
    <>
      <MobHeader title="Laboratórios" onBack={onBack}/>
      <div style={{ flex: 1, minHeight: 0, padding: '18px 16px', overflowY: 'auto', background: 'var(--bg)' }}>
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>Escolha o laboratório</h2>
        <p style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 13 }}>
          {loading ? 'Buscando laboratórios…' : labs.length > 0
            ? `${labs.length} laboratório(s) com todos os exames do pedido · ordenados por proximidade`
            : 'Nenhum laboratório faz todos os exames — veja as combinações'}
        </p>

        {error && (
          <div role="alert" style={{ marginTop: 14, padding: '12px 14px', background: 'var(--primary-tint)', borderRadius: 10, color: 'var(--brand-primary)', fontSize: 13, fontWeight: 600 }}>
            {error}
          </div>
        )}

        {!loading && !error && labs.length === 0 && combos && (
          <CombinationList combos={combos} exams={exams} selectedIdx={comboIdx} onPick={pickCombo}/>
        )}

        {!loading && !error && labs.length > 0 && (
          <>
            <div style={{ position: 'relative', marginTop: 14 }}>
              <Icon.search size={18} color="var(--text-subtle)" style={{ position: 'absolute', left: 14, top: 14 }}/>
              <input className="input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar laboratório pelo nome…" style={{ paddingLeft: 42 }}/>
            </div>

            <div style={{ display: 'flex', gap: 6, marginTop: 14, overflowX: 'auto', paddingBottom: 4 }}>
              {[{ k: 'distance', l: 'Mais próximo' }, { k: 'price-asc', l: 'Menor preço' }, { k: 'price-desc', l: 'Maior preço' }].map((t) => (
                <button key={t.k} onClick={() => setSort(t.k)} style={{
                  padding: '7px 12px', borderRadius: 99, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                  background: sort === t.k ? 'var(--midnight)' : 'var(--white)',
                  color: sort === t.k ? 'var(--white)' : 'var(--text-muted)',
                  border: '1px solid ' + (sort === t.k ? 'var(--midnight)' : 'var(--border)'),
                  fontFamily: 'var(--font-display)',
                }}>{t.l}</button>
              ))}
            </div>
          </>
        )}

        {!loading && !error && labs.length > 0 && sorted.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 16px', color: 'var(--text-subtle)', fontSize: 13.5 }}>
            Nenhum laboratório encontrado para "{q}".
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
          {sorted.map(lab => {
            const isSel = selected === lab.id;
            return (
              <div key={lab.id} onClick={() => pick(lab)} style={{ padding: 14, borderRadius: 14, cursor: 'pointer', background: 'var(--white)', border: '2px solid ' + (isSel ? 'var(--brand-primary)' : 'var(--border)'), boxShadow: isSel ? '0 4px 16px var(--overlay-primary-12)' : 'none' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, background: 'linear-gradient(135deg, var(--midnight), var(--brand-primary))', color: 'var(--white)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14 }}>
                    {lab.name.split(' ').map(w => w[0]).slice(0,2).join('')}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                      <h3 style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}>{lab.name}</h3>
                      <DistanceChip km={lab.distanceKm}/>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{lab.address}</div>
                    {lab.hours && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{lab.hours}</div>}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                  <div>
                    {lab.savings > 0 && <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--green-dark)', marginBottom: 2 }}>{lab.savings}% abaixo da média dos parceiros</div>}
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, fontFamily: 'var(--font-display)' }}>Total {exams.length} exame(s)</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: isSel ? 'var(--brand-primary)' : 'var(--midnight)', lineHeight: 1 }}>
                      R$ {lab.total.toFixed(2).replace('.', ',')}
                    </div>
                  </div>
                  <div style={{ padding: '8px 14px', borderRadius: 99, background: isSel ? 'var(--brand-primary)' : 'var(--marble-soft)', color: isSel ? 'var(--white)' : 'var(--midnight)', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-display)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {isSel ? <><Icon.check size={14} color="var(--white)" strokeWidth={3}/> Selecionado</> : 'Selecionar'}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <FooterBar
        onBack={onBack}
        onNext={onNext}
        canNext={(!!selected && labs.length > 0) || (labs.length === 0 && comboIdx !== null)}
        loading={loading}
        hint={!loading && labs.length === 0 && combos && combos.combinations.length === 0
          ? 'Nenhum laboratório disponível para este pedido'
          : (!loading && labs.length === 0 && comboIdx === null ? 'Escolha uma combinação' : null)}
      />
    </>
  );
}

const fmtBRL = n => Number(n).toFixed(2).replace('.', ',');

// Distância em destaque: a combinação "recomendada" prioriza uma coleta só de
// sangue, mesmo que o laboratório fique mais longe — o paciente decide.
function DistanceChip({ km }) {
  if (km == null) return null;
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'var(--midnight)', color: 'var(--white)', whiteSpace: 'nowrap', fontFamily: 'var(--font-display)', flexShrink: 0 }}>
      {String(km).replace('.', ',')} km
    </span>
  );
}

// Mais de um laboratório: cada exame só pode ser feito (ou entregue) no
// laboratório indicado para ele; sangue dividido = mais de uma punção.
export function ComboPrepNote({ bloodLabCount }) {
  return (
    <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 10, background: 'var(--warning-tint-2)', fontSize: 12, color: 'var(--warning-darker)', lineHeight: 1.45 }}>
      {bloodLabCount > 1 && (
        <div><b>Você terá {bloodLabCount} coletas de sangue</b>, uma em cada laboratório. Se algum exame pedir jejum, ele vale para cada ida.</div>
      )}
      <div style={{ marginTop: bloodLabCount > 1 ? 4 : 0 }}>
        Cada exame só é feito no laboratório indicado para ele. Amostras colhidas em casa (urina, fezes) devem ser entregues nesse laboratório.
      </div>
    </div>
  );
}

// Nenhum laboratório oferece o pedido inteiro: aviso + combinações sugeridas.
// A API já ordena priorizando exames de sangue no mesmo laboratório (uma
// coleta só), depois menos laboratórios, menor preço e menor distância.
function CombinationList({ combos, exams, selectedIdx, onPick }) {
  const missing = combos.missing || [];
  const list = combos.combinations || [];
  return (
    <>
      <div role="status" style={{ marginTop: 14, padding: '12px 14px', background: 'var(--warning-tint)', borderRadius: 12, fontSize: 13, color: 'var(--warning-darkest)', display: 'flex', gap: 10 }}>
        <Icon.info size={18} color="var(--warning-dark)" style={{ flexShrink: 0, marginTop: 1 }}/>
        <div>
          <b>Nenhum laboratório parceiro faz todos os exames do seu pedido.</b>
          {list.length > 0
            ? ' Sugerimos combinações de laboratórios — sempre que possível, com todos os exames de sangue no mesmo lugar, para você ser furado uma vez só.'
            : ' Volte e ajuste a lista de exames ou o local de busca.'}
        </div>
      </div>

      {missing.length > 0 && (
        <div style={{ marginTop: 10, padding: '12px 14px', background: 'var(--primary-tint)', borderRadius: 12, fontSize: 13, color: 'var(--primary-tint-text)' }}>
          <b>Sem oferta na região:</b> {missing.map(m => m.tuss_name).join(', ')}.
          {list.length > 0 && ' Esses exames ficam fora desta compra.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
        {list.map((c, idx) => {
          const isSel = selectedIdx === idx;
          return (
            <div key={idx} onClick={() => onPick(c, idx)} style={{ padding: 14, borderRadius: 14, cursor: 'pointer', background: 'var(--white)', border: '2px solid ' + (isSel ? 'var(--brand-primary)' : 'var(--border)'), boxShadow: isSel ? '0 4px 16px var(--overlay-primary-12)' : 'none' }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {idx === 0 && <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: 'var(--green-soft)', color: 'var(--green-dark)' }}>RECOMENDADA</span>}
                {c.has_blood && c.blood_together && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: 'var(--marble-soft)', color: 'var(--midnight)' }}>
                    <Icon.check size={11} color="var(--green-dark)" strokeWidth={3}/> Exames de sangue numa coleta só
                  </span>
                )}
                {c.has_blood && !c.blood_together && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: 'var(--warning-tint-2)', color: 'var(--warning-darker)' }}>
                    Coleta de sangue em {c.blood_lab_count} laboratórios
                  </span>
                )}
                <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: 'var(--marble-soft)', color: 'var(--text-muted)' }}>
                  {c.lab_count} {c.lab_count > 1 ? 'laboratórios' : 'laboratório'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {c.labs.map(l => (
                  <div key={l.partner.user_id} style={{ padding: 10, borderRadius: 10, background: 'var(--marble-soft)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14 }}>{l.partner.trade_name}</span>
                          <DistanceChip km={l.partner.distance_km}/>
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                          {[l.partner.address, l.partner.neighborhood, l.partner.city].filter(Boolean).join(', ')}
                        </div>
                      </div>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap' }}>R$ {fmtBRL(l.subtotal)}</div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                      {l.exams.map(e => e.tuss_name + (e.is_blood ? ' 🩸' : '')).join(' · ')}
                    </div>
                  </div>
                ))}
              </div>
              {c.lab_count > 1 && <ComboPrepNote bloodLabCount={c.has_blood ? c.blood_lab_count : 0}/>}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, fontFamily: 'var(--font-display)' }}>
                    Total {c.labs.reduce((n, l) => n + l.exams.length, 0)} de {exams.length} exame(s) · um pagamento
                  </div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: isSel ? 'var(--brand-primary)' : 'var(--midnight)', lineHeight: 1 }}>
                    R$ {fmtBRL(c.total)}
                  </div>
                </div>
                <div style={{ padding: '8px 14px', borderRadius: 99, background: isSel ? 'var(--brand-primary)' : 'var(--marble-soft)', color: isSel ? 'var(--white)' : 'var(--midnight)', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-display)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {isSel ? <><Icon.check size={14} color="var(--white)" strokeWidth={3}/> Selecionada</> : 'Selecionar'}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
